<template>
    <div>
        <SkipToContent />
        <QuitQuizModal
            v-if="showQuitQuizModal && currentExam"
            :quiz-mode="quizModeForQuestion"
            :num-answers="orderedCompleteAnswers.length"
            :quiz-length="quizLength"
            @close="showQuitQuizModal = false"
            @submit="submitQuiz(true)"
            @quit="quitQuiz"
        />
        <QuitAssignmentModal
            v-if="showQuitAssignmentModal && currentExam"
            :num-of-answered-questions="orderedCompleteAnswers.length"
            :num-of-assignment-questions="quizLength"
            @close="showQuitAssignmentModal = false"
            @submit="submitQuiz(true)"
            @exit="quitQuiz"
        />
        <SubmitQuizModal
            v-if="showSubmitQuizModal && currentExam"
            :num-answers="orderedCompleteAnswers.length"
            :quiz-length="quizLength"
            :is-recall-challenge-quiz="isRecallChallengeQuiz"
            @close="showSubmitQuizModal = false"
            @continue="showSubmitQuizModal = false"
            @submit="submitQuiz(true)"
        />
        <SubmitAssignmentModal
            v-if="showSubmitAssignmentModal && currentExam"
            :num-of-answered-questions="orderedCompleteAnswers.length"
            :num-of-assignment-questions="quizLength"
            @submit="submitQuiz(true)"
            @close="showSubmitAssignmentModal = false"
            @cancel="showSubmitAssignmentModal = false"
        />
        <PastDueAssignmentModal
            v-if="showPastDueAssignmentModal"
            :num-of-answered-questions="orderedCompleteAnswers.length"
            :num-of-assignment-questions="quizLength"
            @viewAssignmentResults="submitQuiz(true)"
        />
        <KeywordDefinition
            v-if="showKeywordDefinition && visibleKeywordDefinition && questionContainer && keywordDefinitionParams"
            :keywordDOMElement="keywordDefinitionParams.keywordDOMElement"
            :keywordClickLocation="keywordDefinitionParams.keywordClickLocation"
            :keyword="visibleKeywordDefinition.keyword"
            :definition="visibleKeywordDefinition.definition"
            :questionContainer="questionContainer"
            :serial="currentQuestion.serial"
            :keywordLocation="visibleKeywordDefinition.location"
            @close="closeDefinition"
        />
        <QuizContainer
            ref="quizContainer"
            class="quiz"
            :class="{ 'quiz--progress-bar': showProgressBar }"
            :is-dark-mode="isDarkMode"
            :custom-header-styles="showProgressBar && breakpoint === 'black-bear' ? { height: '26px' } : undefined"
            :custom-main-styles="
                showProgressBar && breakpoint === 'black-bear'
                    ? { height: 'calc(100% - 80px)' }
                    : undefined
            "
            :show-right-side-bar="isAITutorChatOpen"
        >
            <template #header>
                <div class="quiz__exam-info">
                    <ExamMenuCard
                        v-if="currentExam && currentBundle"
                        :exam="currentExam"
                        :bundle-id="currentBundle.objectId"
                        theme="silver"
                        :is-clickable="false"
                        class="quiz__exam-info-card"
                    />
                </div>
                <div
                    v-if="showProgressBar && !showLevelUpResult"
                    class="quiz__progress"
                >
                    <QuizProgress
                        :num-questions="quizLength"
                        :selected-index="questionIndex + 1"
                        :answered-index-list="answeredIndexList"
                        @barClicked="progressBarClicked"
                    />
                </div>
                <div
                    v-if="showTimer"
                    class="quiz__timer"
                >
                    <Icon class="quiz__timer-icon" type="hourglass" />
                    {{ timeDisplay }}
                </div>
                <PocketButton
                    v-if="!showLevelUpResult"
                    class="quiz__quit-btn"
                    type="secondary-yellow-dark"
                    :is-dark-mode="isDarkMode"
                    @click="quitQuizClicked"
                >
                    {{ mode !== 'assignment' ? 'Quit Quiz' : 'Quit Assignment' }}
                </PocketButton>
            </template>
            <template #question>
                <MotivationalMoment
                    v-if="showMotivationalMoment"
                    @close="showMotivationalMoment = false"
                />
                <LevelUpResult
                    v-if="!isLoading && showLevelUpResult"
                    :initialLevelProgress="initialLevelProgress"
                    :scoreIncrease="levelScoreIncrease"
                    :levelsInSubjectCount="levelsInSubjectCount || 0"
                    :incompleteSubjectsCount="incompleteSubjectsCount || 0"
                    @continue="goToQuizResults"
                />
                <RecallChallengeScoringLoading v-else-if="isLoadingRecallChallengeScore" />
                <Icon
                    v-else-if="(isLoading || !currentExam) && !isLoadingRecallChallengeScore && !showLevelUpResult"
                    v-dark
                    class="quiz__loading"
                    type="loading2"
                    :is-dark-mode="isDarkMode"
                />
                <Question
                    v-else-if="mode && currentQuestion && currentExam && !isLoadingRecallChallengeScore"
                    :key="`${currentQuestion.objectId}-${allowKeyboardShortcuts}`"
                    class="quiz__question"
                    :ref="(el) => { if (el && currentQuestion) questionContainerRefs[currentQuestion.objectId] = el }"
                    :container-el="quizContainerEl"
                    :question="currentQuestion"
                    :question-number="questionIndex + 1"
                    :quiz-length="(mode === 'timed' && !secondsRemaining) ? questionIndex + 1 : quizLength"
                    :quiz-mode="quizModeForQuestion"
                    :image-url-prefix="`${s3Url || ''}/${currentQuestion.compositeKey.toUpperCase()}/`"
                    :hide-answer="showAnswersAtEnd"
                    :show-check-answer="!showAnswersAtEnd && showCheckAnswerButton"
                    :previous-choices="previousChoicesTyped"
                    :previous-matrix-choices="previousMatrixChoicesTyped"
                    :previous-build-list-choices="previousBuildListChoicesTyped"
                    :previous-multi-part-multiple-choice-choices="previousChoicesTyped"
                    :initial-show-answers="
                        !showAnswersAtEnd
                            && !!currentAnswer
                            && (!showCheckAnswerButton || (showCheckAnswerButton && currentAnswer.checked))
                            && !(currentQuestion.type === 'Multiple Correct Response' && !currentAnswer.checked)
                            && !(currentQuestion.type === 'Matrix Checkbox' && !currentAnswer.checked)
                            && !(currentQuestion.type === 'Matrix Radio Button' && !currentAnswer.checked)
                            && !(currentQuestion.type === 'Build List' && !currentAnswer.checked)
                            && !(currentQuestion.type === 'Multi-Part Multiple Choice' && !currentAnswer.checked)
                    "
                    :allow-keyboard-shortcuts="allowKeyboardShortcuts"
                    :is-dark-mode="isDarkMode"
                    :answer-seed="currentQuestionSeed"
                    :hide-references="currentExam.hideReferences"
                    :keyword-definitions="keywordDefinitions"
                    :show-right-side-bar="isAITutorChatOpen"
                    :show-tutor-chat-explanation-prompts="!!canShowAITutor"
                    @close="quitQuizClicked"
                    @previousQuestion="clickPrevious"
                    @nextQuestion="clickNext"
                    @checkAnswer="checkAnswer"
                    @selectedChoices="questionAnswered"
                    @submitQuiz="submitQuiz()"
                    @keywordClick="keywordClicked"
                    @update:showExplanation="explanationViewToggled"
                >
                    <template
                        #keywordDefinitionHint
                        v-if="[0, 4, 7].includes(seenQuizWithKeywordsCount)
                            && !isLoading
                            && !hasClickedKeyword
                            && isFirstQuestionWithKeywordInQuiz
                            && !currentAnswer?.checked
                            && !showAnswersAtEnd
                            && !isMobileOrTabletScreen"
                    >
                        <KeywordDefinitionHint
                            :isDarkMode="isDarkMode"
                        />
                    </template>
                    <template #explanationTopExperiment>
                        <KeywordDefinitionBanner
                            v-if="[0, 4, 7].includes(seenQuizWithKeywordsCount)
                                && !hasClickedKeyword
                                && ((isMobileOrTabletScreen && isFirstQuestionWithKeywordInQuiz)
                                    || isSecondQuestionWithKeywordInQuiz)
                                && currentAnswer"
                            :isMobile="breakpoint === 'black-bear'"
                            :isDarkMode="isDarkMode"
                        ></KeywordDefinitionBanner>
                    </template>
                    <template
                        #tag
                        v-if="showLearningTag"
                    >
                        <Tag
                            class="quiz__learning-tag"
                            :is-learning-tag="true"
                            :size="'medium'"
                            :is-dark-mode="isDarkMode"
                        >
                            <template #tagMessage>
                                Learning
                            </template>
                        </Tag>
                    </template>
                    <template #tutorChatExplanationPrompts>
                        <TutorExplanationPrompts
                            v-if="canShowAITutor"
                            @toggleTutorChat="tutorExplanationPromptsTutorIconClick"
                            @selectedTutorExplanationPrompt="sendTutorExplanationPrompt"
                        />
                    </template>
                    <template
                        #action
                        v-if="mode === 'assignment' && showSubmitQuizButton"
                    >
                        <PocketButton
                            :is-dark-mode="isDarkMode"
                            :disabled="orderedCompleteAnswers.length === 0"
                            @click="submitQuiz(false)"
                        >
                            Submit Quiz
                        </PocketButton>
                    </template>
                    <template
                        #action
                        v-else-if="showSubmitQuizButton || (showAnswersAtEnd && isNextArrowDisabled)"
                    >
                        <PocketButton
                            :is-dark-mode="isDarkMode"
                            :disabled="orderedCompleteAnswers.length === 0"
                            @click="submitQuiz()"
                        >
                            Submit Quiz
                        </PocketButton>
                    </template>
                </Question>
            </template>
            <template
                v-if="!showLevelUpResult && !isLoadingRecallChallengeScore"
                #footer
            >
                <div class="quiz__controls">
                    <Icon
                        class="quiz__previous-arrow"
                        :class="{
                            'quiz__previous-arrow--disabled': isPreviousArrowDisabled,
                        }"
                        :tabindex="isPreviousArrowDisabled ? -1 : 0"
                        type="paginationArrow"
                        role="button"
                        aria-label="Previous question"
                        :aria-disabled="isPreviousArrowDisabled"
                        @click="clickPrevious"
                        @keydown.enter.stop.prevent="clickPrevious"
                        @mousedown.prevent
                    />
                    <img
                        v-if="!isLoading && canShowAITutor && breakpoint === 'black-bear'"
                        class="quiz__tutor-mobile-toggle-btn"
                        tabindex="0"
                        role="button"
                        aria-label="Open tutor"
                        type="tutorChat"
                        src="@/assets/quiz/mobile-chat-toolbar-icon.svg"
                        alt=""
                        @click="toggleTutorChat"
                        @keydown.enter.stop.prevent="toggleTutorChat"
                        @keydown.space.stop.prevent="toggleTutorChat"
                        @mousedown.prevent
                    >
                    <div class="quiz__flag-toggle" :class="{ 'quiz__flag-toggle--ai-tutor': canShowAITutor }">
                        <FlagToggle
                            :key="`flagToggle_${allowKeyboardShortcuts}`"
                            :is-flagged="isFlagged"
                            :disabled="!!errorMessage || showMotivationalMoment"
                            :is-dark-mode="isDarkMode"
                            :enable-flag-keyboard-shortcut="allowKeyboardShortcuts"
                            :enable-flag-tooltip="breakpoint !== 'brown-bear' && breakpoint !== 'black-bear'"
                            @toggleFlag="toggleFlag"
                        />
                    </div>
                    <Icon
                        :tabindex="isNextArrowDisabled ? -1 : 0"
                        role="button"
                        aria-label="Next question"
                        :aria-disabled="isNextArrowDisabled"
                        class="quiz__next-arrow"
                        :class="{
                            'quiz__next-arrow--disabled': isNextArrowDisabled,
                        }"
                        type="paginationArrow"
                        @click="clickNext"
                        @keydown.enter.stop.prevent="clickNext"
                        @mousedown.prevent
                    />
                </div>
                <KeyboardShortcutsButton
                    class="quiz__keyboard-shortcuts-btn"
                    :class="{
                        'quiz__keyboard-shortcuts-btn--position-left': canShowAITutor,
                    }"
                    :tooltip-theme="keyboardShortcutsToolTipAlignment"
                    :disabled="showMotivationalMoment"
                    :is-dark-mode="isDarkMode"
                    @keydown.enter.stop
                >
                    <template
                        #keyboardShortcutsModal="{
                            showKeyboardShortcutsModal,
                            closeModalFunc
                        }"
                    >
                        <Portal to="modal">
                            <KeyboardShortcutsModal
                                v-if="showKeyboardShortcutsModal"
                                key="quizKeyboardShortcutsModal"
                                :allow-keyboard-shortcuts="allowKeyboardShortcuts"
                                :is-dark-mode="isDarkMode"
                                aria-live="polite"
                                @toggleKeyboardShortcuts="toggleKeyboardShortcuts"
                                @close="closeModalFunc"
                            />
                        </Portal>
                    </template>
                </KeyboardShortcutsButton>
                <TutorNudge
                    v-if="!isLoading && canShowAITutor"
                    class="quiz__tutor-nudge"
                    :current-question="currentQuestion"
                    :can-show-tutor-chat="!!canShowAITutor"
                    :is-current-question-complete="isCurrentQuestionComplete"
                    :level-up-learning-tag-question="!!levelUpLearningTagQuestion"
                    :current-answer-checked="!!currentAnswer?.checked"
                    :current-answer-is-correct="!!currentAnswer?.isCorrect"
                    :has-selected-choice="hasSelectedChoice"
                    :is-explanation-open="isExplanationOpen"
                    :current-exam="currentExam"
                    :quiz-mode="mode"
                    @openTutor="toggleTutorChat"
                />
                <div
                    v-if="canShowAITutor && !isLoading"
                    class="quiz__tutor-toggle-btn"
                    tabindex="0"
                    role="button"
                    aria-label="Open Tutor"
                    @click="toggleTutorChat"
                    @keydown.enter.stop.prevent="toggleTutorChat"
                    @keydown.space.stop.prevent="toggleTutorChat"
                    @mousedown.prevent
                >
                    Tutor
                    <Icon
                        type="tutorChat"
                        class="quiz__tutor-toggle-btn-icon"
                    />
                </div>
            </template>
            <template #rightSide>
                <TutorChat
                    v-if="!isLoading"
                    ref="tutorChatRef"
                    :question-index="questionIndex + 1"
                    :current-question="currentQuestion"
                    :current-exam="currentExam"
                    :quiz-mode="mode"
                    :explanation-prompt-to-send="pendingExplanationPromptMessage"
                    :learning-tag-question="!!levelUpLearningTagQuestion"
                    :current-question-answer-seed="currentQuestionSeed"
                    @promptMessageSent="pendingExplanationPromptMessage = null"
                />
            </template>
        </QuizContainer>
    </div>
</template>

<script setup lang="ts">
import { ref, computed, watch, nextTick, onMounted, onBeforeUnmount } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import UIKit from '@pocketprep/ui-kit'
import { examMetadataModule } from '@/store/examMetadata/module'
import { bundleModule } from '@/store/bundle/module'
import { quizModule } from '@/store/quiz/module'
import { userExamMetadataModule } from '@/store/userExamMetadata/module'
import type { Study, Teach } from '@pocketprep/types'
import type { ClientBundle, ClientExam } from '@/store/bundle/state'
import { userModule } from '@/store/user/module'
import {
    difference,
    buildListDifference,
    localISODateString,
    getNumericQuizMode,
} from '@/utils'
import QuitQuizModal from '@/components/Quiz/QuitQuizModal.vue'
import SubmitQuizModal from '@/components/Quiz/SubmitQuizModal.vue'
import KeywordDefinition from '@/components/KeywordDefinitions/KeywordDefinition.vue'
import KeywordDefinitionBanner from '@/components/KeywordDefinitions/KeywordDefinitionBanner.vue'
import KeywordDefinitionHint from '@/components/KeywordDefinitions/KeywordDefinitionHint.vue'
import LevelUpResult from '@/components/Quiz/LevelUpResult.vue'
import { utils } from '@pocketprep/ui-kit'
import { screenModule } from '@/store/screen/module'
import SkipToContent from '@/components/SkipToContent.vue'
import { questionModule } from '@/store/question/module'
import { analyticsModule } from '@/store/analytics/module'
import { subscriptionModule } from '@/store/subscription/module'
import { keywordsModule } from '@/store/keywords/module'
import PastDueAssignmentModal from '@/components/Assignments/PastDueAssignmentModal.vue'
import QuitAssignmentModal from '@/components/Assignments/QuitAssignmentModal.vue'
import SubmitAssignmentModal from '@/components/Assignments/SubmitAssignmentModal.vue'
import { tutorModule } from '@/store/tutor/module'
import RecallChallengeScoringLoading from '@/components/RecallChallenge/RecallChallengeScoringLoading.vue'
import MotivationalMoment from '@/components/Quiz/MotivationalMoment.vue'
import TutorChat from '@/components/Tutor/TutorChat.vue'
import TutorExplanationPrompts from '@/components/Tutor/TutorExplanationPrompts.vue'
import TutorNudge from '@/components/Tutor/TutorNudge.vue'

const route = useRoute()
const router = useRouter()

const {
    Button: PocketButton,
    FlagToggle,
    KeyboardShortcutsButton,
    KeyboardShortcutsModal,
    Icon,
    ExamMenuCard,
    QuizContainer,
    Question,
    QuizProgress,
    Tag,
} = UIKit

// UIKit Question expects a narrower TQuizMode than Study
type QuestionQuizMode = 'qotd' | 'quick10' | 'timed' | 'weakest' | 'missed' | 'custom'

// UIKit Question component ChoiceKey props
type TChoiceKey = `${'a' | 'd'}${1 | 2 | 3 | 4 | 5 | 6 | 7 | 8 | 9}`
type TMatrixChoiceKey = `${'a' | 'd'}${1 | 2 | 3}_${1 | 2 | 3 | 4 | 5 | 6 | 7 | 8 | 9 | 10 | 11 | 12}`
type TBuildListChoiceKey = `a${1 | 2 | 3 | 4 | 5 | 6 | 7 | 8 | 9}`

const isLoading = ref(true)
const questionIndex = ref(0)
const timerInterval = ref<ReturnType<typeof setInterval> | null>(null)
const showQuitQuizModal = ref(false)
const showQuitAssignmentModal = ref(false)
const showSubmitQuizModal = ref(false)
const showSubmitAssignmentModal = ref(false)
const showLevelUpResult = ref(false)
const hideMotivationalMoment = ref(false)  // Can force motivational moment to disappear
const currentExam = ref<null | Study.Class.ExamMetadataJSON | ClientExam>(null)
const currentBundle = ref<null | Study.Class.BundleJSON | ClientBundle>(null)
const fromName = ref<string | null>(null)
const isFlagged = ref(false)
const isTogglingFlag = ref(false)
const hasCheckedAnswer = ref(false)
const recordedQuizId = ref<string | null>(null)
const isLevelUp = ref(false)
const initialLevelProgress = ref<Study.Cloud.TLevelUpProgress[number] | null>(null)
const levelScoreIncrease = ref(0)
const subjectsWithLevels = ref<{
    subjectId?: string
    levels: number[]
}[]>([])
const levelsInSubjectCount = ref<number | null>(null)
const incompleteSubjectsCount = ref<number | null>(null)
const keywordDOMElement = ref<HTMLElement | null>(null)
const keywordClickLocation = ref<{ x: number; y: number } | null>(null)
const showKeywordDefinition = ref(false)
const visibleKeywordDefinition = ref<{
    keyword: string
    definition: string
    location: string
    element: HTMLElement
} | null>(null)
const hasClickedKeywordDefinitionDuringQuiz = ref(false)
const showPastDueAssignmentModal = ref(false)
const errorMessage = ref('')
const clientVersion = import.meta.env.VUE_APP_VERSION
const isLoadingRecallChallengeScore = ref(false)
const showMotivationalMoment = ref(false)
const hasSeenHighlightedKeyword = ref(false)
const pendingExplanationPromptMessage = ref<string | null>(null)
const isExplanationOpen = ref(false)
const quizContainer = ref<InstanceType<typeof UIKit.QuizContainer> | null>(null)
const questionContainerRefs = ref<Record<string, unknown>>({})
const tutorChatRef = ref<InstanceType<typeof TutorChat> | null>(null)

const keywordDefinitionParams = computed(() => {
    const el = keywordDOMElement.value
    const loc = keywordClickLocation.value
    if (!el || !loc) return null
    return { keywordDOMElement: el, keywordClickLocation: loc }
})

const isAITutorChatOpen = computed(() => {
    return tutorModule.state.isChatOpen
})

const canShowAITutor = computed(() => {
    const pocketbrainUrlEnvVariable = import.meta.env.VUE_APP_POCKETBRAIN_URL
    return route.name === 'quiz'
        && activeQuiz.value
        && activeQuiz.value.mode !== 'mockExam'
        && activeQuiz.value.mode !== 'assignment'
        && !!pocketbrainUrlEnvVariable
})

const chatUuid = computed(() => {
    return tutorModule.getters.getChatUuid(currentQuestion.value?.serial, currentExam.value?.objectId)
})

const hasSelectedChoice = computed(() => {
    return !!currentAnswer.value?.selectedChoices?.length
})

const isDarkMode = computed(() => {
    return userModule.state.settings.isDarkMode
})

const breakpoint = computed(() => {
    return screenModule.getters.getBreakpoint()
})

const questionContainer = computed(() => {
    const questionContainerRef = currentQuestion.value
        ? questionContainerRefs.value[currentQuestion.value.objectId]
        : undefined
    const refWithEl = questionContainerRef as { $el?: HTMLElement } | undefined
    return refWithEl?.$el ?? null
})

const quizContainerEl = computed(() => {
    const quizContainerRef = quizContainer.value
    return quizContainerRef && '$el' in quizContainerRef ? quizContainerRef.$el as HTMLElement : null
})

const keyboardShortcutsToolTipAlignment = computed(() => {
    if (breakpoint.value !== 'brown-bear' && breakpoint.value !== 'black-bear') {
        return canShowAITutor.value ? 'rightalign' : 'leftalign'
    }
    return undefined
})

const activeQuiz = computed(() => {
    return quizModule.getters.getActiveQuiz()
})

const mode = computed(() => {
    return activeQuiz.value?.mode || null
})

// UIKit Question component expects narrower quiz-mode type than Study
const quizModeForQuestion = computed<QuestionQuizMode | undefined>(() => {
    return (mode.value || undefined) as QuestionQuizMode | undefined
})

const showProgressBar = computed(() => {
    // Hide progress bar if quiz mode is 'timed'
    return mode.value !== 'timed'
})

const showTimer = computed(() => {
    // Show timer if quiz mode is 'timed'
    return mode.value === 'timed'
})

const levelUpLearningTagQuestion = computed(() => {
    return showLearningTag.value && mode.value === 'levelUp'
})

const isQuestionComplete = (questionSerial: string) => {
    const question = activeQuizQuestions.value.find(q => q.serial === questionSerial)
    const answer = answers.value.find(a => a.questionSerial === questionSerial)
    if (!question || !answer) {
        return false
    }

    // Matrix questions are complete if each row has a selected choice
    if ([ 'Matrix Radio Button', 'Matrix Checkbox' ].includes(question.type)) {
        const answeredRowNums = answer.selectedChoices.map(choice => {
            return Number(choice.split('_')[0].slice(1))    // a1_2 -> 1
        })
        const matrixRows = question.matrixChoiceLayout || []
        const isEveryRowAnswered = matrixRows.every((row, index) => {
            const rowNum = index + 1
            const hasSelectedChoiceForRow = answeredRowNums.includes(rowNum)
            return hasSelectedChoiceForRow
        })
        return isEveryRowAnswered
    }

    // MPMC questions are answered if each part (mpmc label) has a selected choice
    if (question.type === 'Multi-Part Multiple Choice' && question.mpmcLabels) {
        const selectedChoices = answer.selectedChoices
        const mpmcLabels = question.mpmcLabels
        const selectedMPMCLabelIndexes: number[] = []

        selectedChoices.forEach(choice => {
            const mpmcChoice = question.choices.find(c => c.id === choice)
            if (
                mpmcChoice?.labelIndex !== undefined
                && !selectedMPMCLabelIndexes.includes(mpmcChoice?.labelIndex)
            ) {
                selectedMPMCLabelIndexes.push(mpmcChoice?.labelIndex)
            }
        })

        return mpmcLabels.length === selectedMPMCLabelIndexes.length
    }

    // Other question types are complete if they have 1 or more selected choice
    return answer.selectedChoices.length > 0
}

const isCurrentQuestionComplete = computed(() => {
    const serial = currentQuestion.value?.serial
    return serial ? isQuestionComplete(serial) : false
})

const activeQuizQuestions = computed(() => {
    return activeQuiz.value?.questions || []
})

const questionSerialSet = computed(() => {
    return new Set(activeQuizQuestions.value.map(question => {
        return question.serial
    }))
})

const answers = computed(() => {
    return activeQuiz.value?.answers || []
})

const answerSerialSet = computed(() => {
    return new Set(answers.value.map(answer => {
        return answer.questionSerial
    }))
})

const durationSeconds = computed(() => {
    return activeQuiz.value?.durationSeconds || 0
})

const secondsRemaining = computed(() => {
    return ((activeQuiz.value?.timeLimit || 0) * 60 - (durationSeconds.value || 0))
})

const startedAt = computed(() => {
    return activeQuiz.value?.startedAt
})

const currentQuestion = computed(() => {
    return activeQuizQuestions.value[questionIndex.value] || null
})

const currentQuestionSeed = computed(() => {
    return activeQuiz.value?.answerSeeds?.[questionIndex.value] || null
})

const quizLength = computed(() => {
    return activeQuizQuestions.value.length
})

const timeDisplay = computed(() => {
    if (!secondsRemaining.value) {
        return '0:00'
    }

    const numMinutes = Math.floor(secondsRemaining.value / 60)
    const numSeconds = secondsRemaining.value % 60
    return `${numMinutes}:${String(numSeconds).padStart(2, '0')}`
})

const s3Url = computed(() => {
    return import.meta.env.VUE_APP_S3_URL
})

const flaggedQuestions = computed(() => {
    return userExamMetadataModule.getters.getFlaggedQuestions()
})

const answeredIndexList = computed(() => {
    return activeQuizQuestions.value.reduce<number[]>((acc, question, index) => {
        if (answerSerialSet.value.has(question.serial)) {
            acc.push(index + 1)
        }
        return acc
    }, [])
})

const currentAnswer = computed(() => {
    return answers.value.find(ans => {
        return currentQuestion.value?.serial === ans.questionSerial
    }) || null
})

const previousChoices = computed(() => {
    return currentAnswer.value?.selectedChoices || []
})

// Question component expects literal union types; cast from string[] for type compatibility
const previousChoicesTyped = computed(() => {
    // non matrix and build list questions
    return previousChoices.value as TChoiceKey[]
})

const previousMatrixChoicesTyped = computed(() => {
    return previousChoices.value as TMatrixChoiceKey[]
})

const previousBuildListChoicesTyped = computed(() => {
    return previousChoices.value as TBuildListChoiceKey[]
})

const isPreviousArrowDisabled = computed(() => {
    return questionIndex.value === 0
        || (mode.value === 'timed' && secondsRemaining.value <= 0)
        || (mode.value === 'assignment' && showPastDueAssignmentModal.value)
        || showMotivationalMoment.value
})

const isNextArrowDisabled = computed(() => {
    return (questionIndex.value >= (quizLength.value - 1))
        || (mode.value === 'timed' && secondsRemaining.value <= 0)
        || (mode.value === 'assignment' && showPastDueAssignmentModal.value)
        || showMotivationalMoment.value
})

const quizSettings = computed(() => {
    return userModule.state.user?.quizSettings || null
})

const isMobileOrTabletScreen = computed(() => {
    return breakpoint.value === 'black-bear' || breakpoint.value === 'brown-bear'
})

const seenQuizWithKeywordsCount = computed(() => {
    return userModule.state.user?.webConfig?.seenQuizWithKeywordsCount || 0
})

const isFirstQuestionWithKeywordInQuiz = computed(() => {
    const firstQuizQuestionWithKeywords = activeQuizQuestions.value.find(q => {
        return keywordsModule.getters.getKeywordDefinitions().get(q.serial)
    })?.serial

    return firstQuizQuestionWithKeywords
        ? firstQuizQuestionWithKeywords === currentQuestion.value?.serial
        : false
})

const hasClickedKeyword = computed(() => {
    return userModule.state.user?.webConfig?.hasClickedKeyword
})

const isSecondQuestionWithKeywordInQuiz = computed(() => {
    return activeQuizQuestions.value.filter(q => {
        return keywordsModule.getters.getKeywordDefinitions().get(q.serial)
    })[1]?.serial === currentQuestion.value?.serial
})

const allowKeyboardShortcuts = computed(() => {
    return !!quizSettings.value?.enableKeyboardShortcuts
})

const showAnswersAtEnd = computed(() => {
    if (isLevelUp.value) {
        return false
    } else if (activeQuiz.value && ('showAnswersAtEnd' in activeQuiz.value)) {
        return activeQuiz.value.showAnswersAtEnd
    } else {
        return !!quizSettings.value?.showAnswersAtEnd
    }
})

const showCheckAnswerButton = computed(() => {
    if (activeQuiz.value && ('showCheckAnswerButton' in activeQuiz.value)) {
        return activeQuiz.value.showCheckAnswerButton
    } else if (isLevelUp.value && levelUpLearningTagQuestion.value) {
        // In a level up question and a learning tag question
        return true
    } else {
        return !!quizSettings.value?.showCheckAnswerButton
    }
})

const currentCorrectCount = computed(() => {
    return answers.value.filter(answer => answer.isCorrect).length
})

const bestAnswers = computed(() => {
    if (mode.value === 'levelUp') {
        return quizModule.getters.getLatestAnswers({
            questionFilter: 'all',
            sinceDate: lastLevelUpResetDate.value ? lastLevelUpResetDate.value : undefined,
            quizMode: 6,
        })
    } else {
        return quizModule.getters.getLatestAnswers({ questionFilter: 'all' })
    }
})

const currentBestAnswer = computed(() => {
    return currentQuestion.value ? bestAnswers.value[currentQuestion.value.serial] : null
})

const correctStreakCount = computed(() => {
    let streakCount = 0
    // Start with the most recent answer and move back, counting correct answers until there's an incorrect answer
    for (let i = answers.value.length - 1; i >= 0; i--) {
        if (answers.value[i].isCorrect) {
            streakCount++
        } else {
            break
        }
    }
    return streakCount
})

const showLearningTag = computed(() => {
    return mode.value === 'levelUp'
        && currentBestAnswer.value
        && !currentBestAnswer.value.isCorrect
})

const lastLevelUpResetDate = computed(() => {
    return userExamMetadataModule.getters.getLastLevelUpResetDate()
})

const keywordDefinitions = computed(() => {
    const questionSerial = currentQuestion.value?.serial
    const questionKeywords = questionSerial
        ? keywordsModule.getters.getKeywordDefinitions().get(questionSerial)
        : undefined

    if (
        !currentAnswer.value
        || !questionKeywords
        || !currentAnswer.value.checked
    ) {
        return []
    }

    return Array.from(questionKeywords, ([ keyword, definition ]) => ({
        keyword,
        definition,
    }))
})

const assignment = computed(() => {
    return activeQuiz.value?.prebuiltQuiz
})

const assignmentId = computed(() => {
    return assignment.value?.objectId
})

const assignmentOrgId = computed(() => {
    const assignmentOrg = assignment.value?.organization as Teach.Class.OrganizationJSON
    return assignmentOrg?.objectId
})

const assignmentDueDateIso = computed(() => {
    return assignment.value?.dueDate?.iso
})

const isRecallChallengeQuiz = computed(() => {
    return mode.value === 'recallChallenge'
})

const conceptQuizId = computed(() => {
    return quizModule.state.recommendedNext?.config.conceptQuizId
})

// For amplitude
const subscriptionSource = computed(() => {
    const subscription = subscriptionModule.getters.getSubscriptionForExamId()
    return subscription
        && typeof subscription !== 'boolean'
        && subscription.receipt // Subscription.receipt is `.included in fetchUserData cloud function
        && 'source' in subscription.receipt
        && subscription.receipt.source
})

// We want to make sure the answers are in the same order that the questions were presented
const orderedCompleteAnswers = computed(() => {
    return activeQuizQuestions.value.map(question => {
        if (!isQuestionComplete(question.serial)) {
            return undefined
        }
        const answerForQuestion = answers.value.find(answer => answer.questionSerial === question.serial)
        return answerForQuestion || undefined
    }).filter(answer => {
        return !!answer
    }) as Study.Cloud.IQuizAnswer[]
})

const showSubmitQuizButton = computed(() => {
    return answerSerialSet.value.size >= questionSerialSet.value.size
        && (currentQuestion.value?.type === 'Multiple Correct Response' && !hasCheckedAnswer.value)
})

const prebuiltQuiz = computed(() => {
    return activeQuiz.value?.prebuiltQuiz
})

onMounted(async () => {
    fromName.value = (route.meta.fromName as string | undefined) || null

    if (typeof route.query.assignment === 'string') {
        // Route a user to the Assignment Intro screen if have copied and pasted an assignment link
        if (!activeQuiz.value || (activeQuiz.value?.prebuiltQuiz?.objectId !== route.query.assignment)) {
            await router.push({
                name: 'assignment-intro',
                query: {
                    assignment: route.query.assignment,
                    legacyLinkFollowed: 'true',
                },
            })
            return
        }
    }

    if (!activeQuiz.value) {
        router.push({ name: 'study' })
        return
    }
    // Quit quiz if user is loading a quiz with legacy question schema
    if (!activeQuiz.value.questions[0]?.choices) {
        quitQuiz()
        return
    }

    // Restore place in any previously saved quiz
    questionIndex.value = activeQuiz.value.currentQuestionIndex

    await Promise.all([
        userModule.actions.fetchUserData(),
        quizModule.actions.fetchAnsweredQuestions(),
        questionModule.actions.fetchSerialQuestionInfoLib(),
        bundleModule.actions.fetchBundles(),
        examMetadataModule.actions.fetchExamMetadata(),
        userExamMetadataModule.actions.fetchUserExamMetadata(),
    ])

    if (canShowAITutor.value) {
        await tutorModule.actions.fetchTutorSession()
    }

    if (tutorModule.state.isChatOpen && !tutorModule.state.hasOpenedChatDuringQuiz) {
        tutorModule.state.hasOpenedChatDuringQuiz = true
        tutorModule.state.greetingQuestionIndex = activeQuiz.value?.currentQuestionIndex ?? null
    }

    currentExam.value = examMetadataModule.getters.getCurrentExamMetadata()
    currentBundle.value = bundleModule.getters.getCurrentBundle() || null

    // fetch keyword data
    const serials = activeQuiz.value?.questions.map(q => {
        return q.serial
    }) || []
    await keywordsModule.actions.fetchKeywordDefinitions(serials)

    if (mode.value === 'levelUp' && currentExam.value) {
        subjectsWithLevels.value = examMetadataModule.state.subjectsWithLevels
        isLevelUp.value = true
        const levelUpProgressData = userExamMetadataModule.getters.getLevelUpProgress()
        const subject = currentQuestion.value.subject as Study.Class.SubjectJSON
        const subjectLevelProgress = levelUpProgressData.find(progress => {
            return progress.subjectId === subject.objectId
        })
        levelsInSubjectCount.value = subjectsWithLevels.value.find(subj => {
            return subj.subjectId === subject.objectId
        })?.levels.length || null
        incompleteSubjectsCount.value = levelUpProgressData.filter(progress => {
            const subjectNumLevels = subjectsWithLevels.value
                .find(subjectWithLevels => {
                    return subjectWithLevels.subjectId === progress.subjectId
                })?.levels?.length
            return subjectNumLevels && progress.level <= subjectNumLevels && progress.score < progress.goal
        }).length
        initialLevelProgress.value = subjectLevelProgress ? { ...subjectLevelProgress } : null
    }

    isLoading.value = false

    const questionSerial = currentQuestion.value?.serial
    isFlagged.value = !!(questionSerial && flaggedQuestions.value.includes(questionSerial))

    timerInterval.value = setInterval(() => {
        const activeQuizVal = activeQuiz.value
        if (activeQuizVal) {
            const durationSecondsVal = activeQuizVal.durationSeconds || 0
            const timeLimit = activeQuizVal.timeLimit && activeQuizVal.timeLimit * 60
            if (timeLimit === undefined || durationSecondsVal < timeLimit) {
                activeQuizVal.durationSeconds = durationSecondsVal + 1
                quizModule.actions.updateActiveQuiz(activeQuizVal)

                if (
                    timerInterval.value
                    && typeof timeLimit === 'number'
                    && activeQuizVal.durationSeconds >= timeLimit
                ) {
                    clearInterval(timerInterval.value)
                }
            }

            const assignmentDueDateIsoString = assignmentDueDateIso.value
            const quizStartedAt = startedAt.value
            if (mode.value === 'assignment' && assignmentDueDateIsoString && quizStartedAt) {
                const now = new Date()
                const assignmentDueDate = new Date(assignmentDueDateIsoString)
                const quizStartedAtDate = new Date(quizStartedAt)

                const isPastDue = now.getTime() > assignmentDueDate.getTime()
                const isAssignmentDueDateAfterQuizStart = quizStartedAtDate.getTime() < assignmentDueDate.getTime()
                showPastDueAssignmentModal.value = isPastDue && isAssignmentDueDateAfterQuizStart

                if (timerInterval.value && showPastDueAssignmentModal.value) {
                    assignmentPassDueAmplitudeEvent()
                    clearInterval(timerInterval.value)
                }
            }
        }
    }, 1000)
})

onBeforeUnmount(() => {
    if (timerInterval.value) {
        clearInterval(timerInterval.value)
    }

    if (tutorModule.state.isChatOpen) {
        tutorModule.state.isChatOpen = false
    }
})

const toggleTutorChat = () => {
    tutorModule.actions.toggleTutorChat(currentQuestion.value?.serial, currentExam.value?.objectId)
}

const tutorCannedPromptAmplitudeEvent = (cannedPromptSelected: string) => {
    analyticsModule.actions.amplitudeTrack('Tutor Canned Prompt Clicked', {
        chatUuid: chatUuid.value || '',
        examMetadataId: currentExam.value?.objectId || '',
        examName: currentExam.value?.nativeAppName || '',
        serial: currentQuestion.value.serial || '',
        quizMode: mode.value ? Number(getNumericQuizMode(mode.value)) : 10000,
        cannedPrompt: cannedPromptSelected || '',
    })
}

const tutorExplanationPromptsTutorIconClick = () => {
    if (isAITutorChatOpen.value) {
        tutorChatRef.value?.focusInput()
    } else {
        toggleTutorChat()
    }

    tutorCannedPromptAmplitudeEvent('Ask something else')
}

const sendTutorExplanationPrompt = (message: string) => {
    if (!isAITutorChatOpen.value) {
        tutorModule.actions.toggleTutorChat(currentQuestion.value?.serial, currentExam.value?.objectId)
    }
    pendingExplanationPromptMessage.value = message

    const cannedPromptSelected = message === 'Go deeper into the explanation for this question'
        ? 'Go deeper'
        : message === 'Simplify the explanation for this question'
            ? 'Simplify'
            : 'I still have questions'

    tutorCannedPromptAmplitudeEvent(cannedPromptSelected)
}


const setShowMotivationalMoment = () => {
    showMotivationalMoment.value = !hideMotivationalMoment.value
        && mode.value === 'levelUp'
        && correctStreakCount.value === 8
}

const currentQuestionChanged = () => {
    isFlagged.value = !!currentQuestion.value && flaggedQuestions.value.includes(currentQuestion.value.serial)
}

watch(currentQuestion, () => {
    currentQuestionChanged()
})

const keywordClicked = (params?: {
    keyword: string
    location: string
    clickLocation: { x: number; y: number }
    target: HTMLElement
}) => {
    showKeywordDefinition.value = false
    if (!params?.keyword) {
        return
    }

    const questionKeywords = keywordsModule.getters.getKeywordDefinitions().get(currentQuestion.value.serial)
    const definition = questionKeywords?.get(params.keyword.toLowerCase())

    if (!definition) {
        return
    }

    analyticsModule.actions.amplitudeTrack('Keyword Definition Viewed', {
        examGuid: currentExam.value?.examGuid,
        definitionKeyword: params.keyword,
        serial: currentQuestion.value.serial,
        location: params.location,
        quizMode: mode.value ? Number(getNumericQuizMode(mode.value)) : 10000,
        studyMode: 'Active Quiz',
    })

    visibleKeywordDefinition.value = {
        keyword: params.keyword,
        definition,
        location: params.location,
        element: params.target,
    }
    keywordDOMElement.value = params.target
    keywordClickLocation.value = params.clickLocation
    hasClickedKeywordDefinitionDuringQuiz.value = true

    params.target.setAttribute('aria-expanded', 'true')

    nextTick(() => {
        showKeywordDefinition.value = true
    })
}

const closeDefinition = () => {
    showKeywordDefinition.value = false
    visibleKeywordDefinition.value?.element.setAttribute('aria-expanded', 'false')
}

const quitQuizClicked = () => {
    if (orderedCompleteAnswers.value.length) {
        if (mode.value === 'assignment') {
            showQuitAssignmentModal.value = true
        } else {
            showQuitQuizModal.value = true
        }
    } else {
        quitQuiz()
    }
}

const quitQuiz = () => {
    // Log in amplitude that the user quit the quiz
    if (mode.value) {
        // calculate how many answers in quiz were on "learning" questions
        const isLearningQuestionCount = mode.value === 'levelUp'
            ? answers.value.filter(a => {
                return bestAnswers.value[a.questionSerial]
                    && !bestAnswers.value[a.questionSerial].isCorrect
            }).length
            : 0

        // Percentage rounded: 4 of 10 question = 40
        const percentOfQuizCompleted = Math.round((orderedCompleteAnswers.value.length / quizLength.value) * 100)
        const quizQuitPayload: { [key: string]: string | number | boolean } = {
            isLearningQuestion: !!showLearningTag.value,
            quizPlatform: 'Web',
            quizAnswerCount: orderedCompleteAnswers.value.length,
            isLearningQuestionCount,
            quizTotalQuestionCount: quizLength.value,
            quizPercentComplete: percentOfQuizCompleted,
            examGuid: currentExam.value?.examGuid || '',
            bundleId: currentBundle.value?.objectId || '',
        }

        if (subscriptionSource.value) {
            quizQuitPayload.subscriptionSource = subscriptionSource.value
        }

        if (getNumericQuizMode(mode.value) !== 'unknown') {
            quizQuitPayload.quizMode = Number(getNumericQuizMode(mode.value))
        }

        analyticsModule.actions.amplitudeTrack('Quiz Quit', quizQuitPayload)
    }

    if (tutorModule.state.isChatOpen) {
        tutorModule.state.isChatOpen = false
    }

    quizModule.actions.updateActiveQuiz(null)

    if (localStorage.getItem('tutorNudgesForQuizQuestions')) {
        localStorage.removeItem('tutorNudgesForQuizQuestions')
    }

    tutorModule.state.hasOpenedChatDuringQuiz = false
    tutorModule.state.greetingQuestionIndex = null

    if (
        !fromName.value
        || fromName.value === 'byoq'
        || fromName.value === 'quiz'
        || fromName.value === 'assignment-intro'
        || fromName.value === 'quiz-result'
    ) {
        router.push({
            name: 'study',
        })
    } else {
        router.back()
    }
}

const clickPrevious = () => {
    if ((mode.value === 'timed' && secondsRemaining.value <= 0) || showMotivationalMoment.value) {
        return
    }

    if (questionIndex.value > 0) {
        questionIndex.value--
    }
}

const clickNext = () => {
    if ((mode.value === 'timed' && secondsRemaining.value <= 0) || showMotivationalMoment.value) {
        return
    }

    if (questionIndex.value < (quizLength.value - 1)) {
        questionIndex.value++

        // move focus back to top
        nextTick(() => {
            const headEl = document.querySelector('.uikit-quiz-container__header') as HTMLElement
            const focusableEls = Array.from<HTMLElement>(headEl.querySelectorAll(
                'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
            ))
            focusableEls[focusableEls.length - 1]?.focus()
            focusableEls[focusableEls.length - 1]?.blur()
        })
    }

    setShowMotivationalMoment()
}

const progressBarClicked = (questionNumber: number) => {
    if (
        (mode.value === 'timed' && secondsRemaining.value <= 0)
        || (mode.value === 'assignment' && showPastDueAssignmentModal.value)
    ) {
        return
    }

    questionIndex.value = questionNumber - 1
}

const checkAnswer = (answer: Study.Cloud.IQuizAnswer) => {
    questionAnswered(answer, true)
    hasCheckedAnswer.value = true
}

const questionAnswered = (answer: Study.Cloud.IQuizAnswer, checked?: boolean) => {
    if (!activeQuiz.value) {
        throw new Error('A quiz is active, but no activeQuiz was found')
    }

    const answersList = answers.value
    const existingAnswer = answersList.find(ans => {
        return ans.questionSerial === answer.questionSerial
    })

    if (currentQuestion.value.type === 'Build List' && (
        existingAnswer
        && !checked
        && !buildListDifference(existingAnswer.selectedChoices, answer.selectedChoices).length
        && !buildListDifference(answer.selectedChoices, existingAnswer.selectedChoices).length
    ) || (
        !existingAnswer
        && !answer.selectedChoices.length
    )) {
        // If question component emits selected choices order that we already have, don't make any updates
        // When a user sees a build list there will already be a "selected order" so we want to note
        // if the order is different
        return
    } else if (currentQuestion.value.type !== 'Build List' && (
        existingAnswer
        && !checked
        && !difference(existingAnswer.selectedChoices, answer.selectedChoices).length
        && !difference(answer.selectedChoices, existingAnswer.selectedChoices).length
    ) || (
        !existingAnswer
        && !answer.selectedChoices.length
    )) {
        // If question component emits selected choices that we already have or are empty, don't make any updates
        return
    }

    if (existingAnswer && !answer.selectedChoices.length) {
        // If the user has removed their selections, remove the answer from the list
        const answerIndex = answersList.indexOf(existingAnswer)
        answersList.splice(answerIndex, 1)
    } else if (existingAnswer) {
        // If we have new selected choices for an existing answer, update them on the answer object
        existingAnswer.selectedChoices = answer.selectedChoices
        existingAnswer.isCorrect = answer.isCorrect
        if (checked) {
            existingAnswer.checked = !!checked
        }
    } else {
        // If we have selected choices for a new answer, update the answers list
        answersList.push({
            ...answer,
            checked: !!checked,
        })
    }

    quizModule.actions.updateActiveQuiz({
        ...activeQuiz.value,
        answers: answersList,
    })

    if (keywordDefinitions.value.length) {
        hasSeenHighlightedKeyword.value = true
    }
}

const toggleFlag = async () => {
    // Prevent if already processing toggleQuestionFlag call
    if (isTogglingFlag.value) {
        return
    }

    isTogglingFlag.value = true
    isFlagged.value = !isFlagged.value
    const serial = currentQuestion.value?.serial

    try {
        await userExamMetadataModule.actions.toggleQuestionFlag(serial)
    } catch (e) {
        // noop
    } finally {
        isFlagged.value = !!(serial && flaggedQuestions.value.includes(serial))
        isTogglingFlag.value = false
    }
}

const toggleKeyboardShortcuts = (newVal: boolean) => {
    userModule.actions.updateQuizSettings({
        enableKeyboardShortcuts: newVal,
    })
}

const submitQuiz = async (skipConfirm?: true | false) => {
    showQuitQuizModal.value = false
    showSubmitQuizModal.value = false
    showQuitAssignmentModal.value = false
    showSubmitAssignmentModal.value = false

    if (tutorModule.state.isChatOpen) {
        tutorModule.state.isChatOpen = false
    }
    tutorModule.state.hasOpenedChatDuringQuiz = false
    tutorModule.state.greetingQuestionIndex = null

    if (!currentExam.value) {
        throw new Error('Quiz.submitQuiz: No current exam found')
    }

    if (mode.value === 'assignment' && !skipConfirm) {
        showSubmitAssignmentModal.value = true
        return
    }

    if (isLoading.value || isLoadingRecallChallengeScore.value) {
        return
    }

    // If not all questions have been answered, show confirmation modal
    if (
        !skipConfirm
        && mode.value !== 'timed'
        && mode.value !== 'assignment'
        && answers.value.length < quizLength.value
    ) {
        // Clear the entries upon exit
        showSubmitQuizModal.value = true
        return
    }

    // if user was exposed to a highlighted keyword, update webConfig item
    if (hasSeenHighlightedKeyword.value) {
        userModule.actions.updateWebConfig({
            seenQuizWithKeywordsCount: seenQuizWithKeywordsCount.value + 1,
        })
    }

    const studyModes = utils.studyModes
    const modeEntry = Object.entries(studyModes).find(entry => {
        return entry[1].shortName === mode.value
    })
    const modeCode = modeEntry ? Number(modeEntry[0]) : null
    if (modeCode === null) {
        throw new Error('Quiz.submitQuiz: Cannot submit a quiz without a mode')
    }

    if (isLevelUp.value) {
        isLoading.value = true
        levelScoreIncrease.value = currentCorrectCount.value
        showLevelUpResult.value = true
    }

    const loadingRecallChallengeScoreStartTime = Date.now()
    if (mode.value === 'recallChallenge') {
        isLoadingRecallChallengeScore.value = true
    } else {
        isLoading.value = true
    }

    const subject = currentQuestion.value.subject as Study.Class.SubjectJSON

    // For assignments and recall challenge quizzes, we want to mark any unanswered questions as incorrect
    // Also, we want to make sure the answers are in the same order that the questions were presented
    const fullAnswers: Study.Cloud.IQuizAnswer[] = []
    if (mode.value === 'assignment' || mode.value === 'recallChallenge') {
        activeQuiz.value?.questions.forEach(question => {
            const answerForQuestion = answers.value.find(answer => answer.questionSerial === question.serial)
            if (answerForQuestion) {
                return fullAnswers.push(answerForQuestion)
            }
            fullAnswers.push({
                questionSerial: question.serial,
                selectedChoices: [],
                isCorrect: false,
            })
        })
    }

    // Capture the count here since orderedCompleteAnswers will be cleared when recordQuiz completes
    const recordQuizResponse = await quizModule.actions.recordQuiz({
        answers: mode.value === 'assignment' || mode.value === 'recallChallenge'
            ? fullAnswers
            : orderedCompleteAnswers.value,
        durationSeconds: durationSeconds.value,
        examGuid: currentExam.value.examGuid,
        mode: modeCode,
        platform: 'Web',
        appBundleId: 'study.pocketprep.com',
        startedAt: startedAt.value || localISODateString(),
        examVersion: currentExam.value.version,
        prebuiltQuizId: prebuiltQuiz.value?.objectId,
        examMetadataId: activeQuiz.value?.examMetadataId || currentExam.value.objectId,
        ...(isLevelUp.value && { levelSubjectId: subject.objectId, levelSubjectName: subject.name }),
        conceptQuizId: conceptQuizId.value,
        // Removing this because readiness-report experiment v1 is done, but may use for v2 in 2026
        // includeReadiness: true,
    })
    recordedQuizId.value = recordQuizResponse.quiz.objectId

    if (localStorage.getItem('tutorNudgesForQuizQuestions')) {
        localStorage.removeItem('tutorNudgesForQuizQuestions')
    }

    if (!isLevelUp.value) {
        if (mode.value === 'recallChallenge') {
            // Make sure the loading screen is visible for a minimum time (2 seconds)
            const loadingDuration = Date.now() - loadingRecallChallengeScoreStartTime
            const minimumLoadingDuration = 3000
            if (loadingDuration < minimumLoadingDuration) {
                await new Promise(res => setTimeout(res, minimumLoadingDuration - loadingDuration))
            }
        }

        goToQuizResults()
    }

    isLoading.value = false
}

const goToQuizResults = () => {
    router.push({
        name: 'quiz-result',
        params: { quizId: recordedQuizId.value },
        query: {
            from: fromName.value || null,
        },
    })
}

const questionIndexChanged = async (newQuestionIndex: number) => {
    if (activeQuiz.value) {

        // Hide motivational moment when question changes
        if (showMotivationalMoment.value) {
            hideMotivationalMoment.value = true
        }

        showKeywordDefinition.value = false

        quizModule.actions.updateActiveQuiz({
            ...activeQuiz.value,
            currentQuestionIndex: newQuestionIndex,
        })

    }

    // stop showing keyword messaging for the user once they click their first keyword
    if (hasClickedKeywordDefinitionDuringQuiz.value) {
        await userModule.actions.updateWebConfig({ hasClickedKeyword: true })
    }
}

watch(questionIndex, async (newQuestionIndex) => {
    await questionIndexChanged(newQuestionIndex)
    if (canShowAITutor.value) {
        isExplanationOpen.value = false
        tutorModule.actions.fetchTutorSession()
    }
})

const explanationViewToggled = async (showExplanation: boolean) => {
    isExplanationOpen.value = showExplanation

    // stop showing keyword messaging for the user once they click their first keyword
    if (hasClickedKeywordDefinitionDuringQuiz.value) {
        await userModule.actions.updateWebConfig({ hasClickedKeyword: true })
    }
    if (showExplanation) {
        analyticsModule.actions.amplitudeTrack(
            'Explanation Opened',
            {
                examGuid: currentExam.value?.examGuid,
                bundleId: currentBundle.value?.objectId,
                serial: currentQuestion.value.serial,
                quizMode: mode.value ? Number(getNumericQuizMode(mode.value)) : 10000,
                quizPlatform: 'Web',
                isSharedQotd: false,
                isReviewMode: false,
            }
        )
        if (keywordDefinitions.value) {
            hasSeenHighlightedKeyword.value = true
        }
    }
}

const assignmentPassDueAmplitudeEvent = () => {
    analyticsModule.actions.amplitudeTrack('Assignment Due Date Passed', {
        client: 'Web Study',
        clientAppId: 'study.pocketprep.com',
        clientVersion: clientVersion,
        quizPlatform: 'Web',
        quizAnswerCount: orderedCompleteAnswers.value.length,
        quizTotalQuestionCount: quizLength.value,
        assignmentId: assignmentId.value,
        examGuid: currentExam.value?.examGuid || '',
        bundleId: currentBundle.value?.objectId || '',
        organizationId: assignmentOrgId.value || '',
    })
}
</script>

<style lang="scss" scoped>
.keywords-message {
    position: absolute;
    top: 20%;
    left: 10%;
    z-index: 10000;
}
.quiz {
    position: absolute;
    width: 100%;
    height: 100%;
    box-sizing: border-box;

    &__exam-info {
        position: absolute;
        left: -10px;
        width: 320px;

        @include breakpoint(brown-bear) {
            width: 285px;
        }

        @include breakpoint(black-bear) {
            display: none;
        }
    }

    &__exam-info-card {
        max-width: 100%;
    }

    &__progress {
        position: absolute;
        left: 50%;
        max-width: 768px;
        width: calc(40% + 130px);
        transform: translateX(-50%);

        @include breakpoint(polar-bear) {
            width: calc(40% + 45px);
        }

        @include breakpoint(brown-bear) {
            max-width: 280px;
            width: calc(36% + 38px);
        }

        @include breakpoint(black-bear) {
            max-width: none;
            width: 90%;
        }
    }

    &__timer {
        display: flex;
        align-items: center;
        position: absolute;
        right: 121px;

        @include breakpoint(black-bear) {
            right: auto;
            left: 50%;
            transform: translateX(-50%);
        }
    }

    &__timer-icon {
        margin-right: 12px;
    }

    &__quit-btn {
        position: absolute !important;
        right: 0;

        @include breakpoint(black-bear) {
            display: none;
        }
    }

    &__loading {
        position: absolute;
        left: 50%;
        top: 25%;
        transform: translateX(-50%) scale(1.5);
    }

    &__question {
        position: relative;
        height: 100%;
    }

    &__desktop-bonus-question-explanation-textarea {
        width: 100%;
        margin-top: 20px;

        @include breakpoint(brown-bear) {
            margin-top: 0;
        }
    }

    &__ai-feedback-checkbox {
        box-sizing: border-box;
        width: 100%;
        max-width: calc(min(492px, 100%) - 28px);
        padding-left: 15px;
    }

    :deep(.uikit-checkbox-option__label) {
        font-size: 14px;
    }

    &__ai-feedback-link {
        font-size: 14px;
    }

    &__ai-feedback-error {
        position: relative;
        margin-top: 24px;
        margin-bottom: 253px;
        padding-top: 26px;
        padding-left: 67px;
        border-top: 1px solid $fog;

        @include breakpoint(black-bear) {
            padding-left: 0;
        }
    }

    &__brain-error {
        position: absolute;
        top: 16px;
        left: 0;
        width: 47px;

        @include breakpoint(black-bear) {
            display: none;
        }
    }

    &__ai-feedback-error-message {
        font-size: 16px;
        font-weight: 400;
        line-height: 24px;
        letter-spacing: -0.1px;
        color: $ash;
        margin-bottom: 32px;

        &--dark {
            color: $fog;
        }
    }

    &__controls {
        display: flex;
        align-items: center;
        position: absolute;
        left: 50%;
        transform: translateX(-50%);
        z-index: 2;

        @include breakpoint(black-bear) {
            width: 304px;
            justify-content: center
        }
    }

    &__previous-arrow,
    &__next-arrow {
        &--disabled {
            opacity: 0.35;
        }

        &:not(&--disabled) {
            cursor: pointer;

            @media (hover: hover) {
                &:hover {
                    color: $banana-bread;
                }
            }
        }

        &:focus {
            outline: none;
            color: $banana-bread;
        }
    }

    &__previous-arrow {
        transform: rotate(180deg);
    }

    &__tutor-mobile-toggle-btn {
        margin: 0 11px 0 21px;

        &:hover {
            color: $banana-bread;
            cursor: pointer;
        }
    }

    &__tutor-toggle-btn {
        position: absolute;
        right: 0;
        z-index: 1;
        display: flex;
        align-items: center;
        gap: 9px;
        padding: 9px;
        border-radius: 6px;
        color: $white;
        font-size: 15px;
        font-weight: 600;
        letter-spacing: 0.1px;
        outline: none;
        cursor: pointer;

        &:hover {
            color: $banana-bread;
            background-color: rgba($cream-puff, 0.14);
        }

        @include breakpoint(black-bear) {
            display: none;
        }
    }

    &__flag-toggle {
        display: flex;
        justify-content: space-between;
        align-items: center;
        margin: 0 80px;

        &--ai-tutor {
            @include breakpoint(black-bear) {
                margin: 0 21px 0 10px;
            }
        }
    }

    &__keyboard-shortcuts-btn {
        position: absolute;
        right: 0;

        &--position-left {
            left: 0;
        }

        @include breakpoint(black-bear) {
            display: none;
        }
    }

    :deep(.uikit-question-context__tag) {
        margin-top: 2px;
    }

    :deep(.uikit-question__close-icon) {
        z-index: 3;
    }

    &__tutor-nudge {
        position: absolute;
        right: 24px;
        bottom: 88px;
        z-index: 3;

        @include breakpoint(black-bear) {
            left: 50%;
            right: auto;
            transform: translateX(-50%);
            bottom: 100%;
            margin-bottom: 20px;
            width: calc(100% - 30px);
        }
    }
}
</style>
